import { deleteRequest, patchRequest, postRequest, putRequest } from '../utils'

export const saveProjectFileItem: RequestTypeVariable = {
  getKey: (projectId: string, rootGuid: string) => ['saveProjectFileItem', projectId, rootGuid],
  request: async (
    id: string,
    rootGuid: string,
    data: FileItem & { file: File; pdfPage?: number },
  ): Promise<FileItem> => {
    const formData = new FormData()
    formData.append('file', data.file)
    formData.append('name', data.name)
    formData.append('parent_guid', data.parent_guid)
    formData.append('element_guid', data.element_guid || '')
    // FormData would send undefined as the string "undefined", which is no valid JSON
    if (data.additional_data !== undefined) {
      formData.append('additional_data', JSON.stringify(data.additional_data))
    }
    if (data.pdfPage !== undefined) {
      formData.append('pdf_page', String(data.pdfPage))
    }

    const { data: result } = await putRequest<FileItem>({
      url: `/projects/${id}/files/${rootGuid}`,
      data: formData,
    })

    return result as FileItem
  },
}

export const getPdfPageCount: RequestTypeVariable = {
  getKey: (projectId: string, fileName: string) => ['getPdfPageCount', projectId, fileName],
  request: async (projectId: string, file: File): Promise<{ page_count: number }> => {
    const formData = new FormData()
    formData.append('file', file)

    const { data: result } = await postRequest<{ page_count: number }>({
      url: `/projects/${projectId}/files/pdf-page-count`,
      data: formData,
    })

    return result as { page_count: number }
  },
}

export const deleteProjectFileItem: RequestTypeVariable = {
  getKey: (projectId: string, rootGuid: string, documentId: string) => [
    'deleteProjectFileItem',
    projectId,
    rootGuid,
    documentId,
  ],
  request: async (
    id: string,
    rootGuid: string,
    documentId: string,
  ): Promise<ProjectDocumentsResult> => {
    const { data: result } = await deleteRequest({
      url: `/projects/${id}/files/${rootGuid}/${documentId}`,
    })

    return result as ProjectDocumentsResult
  },
}

export const createProjectFolderItem: RequestTypeVariable = {
  getKey: (projectId: string, rootGuid: string, folderName: string) => [
    'createProjectFolderItem',
    projectId,
    rootGuid,
    folderName,
  ],
  request: async (
    projectId: string,
    rootGuid: string,
    folder: SectionItem,
  ): Promise<SectionItem> => {
    const { data: result } = await postRequest({
      url: `/projects/${projectId}/files/${rootGuid}/folders`,
      data: folder,
    })

    return result as SectionItem
  },
}

export const postFolderConfig: RequestTypeVariable = {
  getKey: (projectId: string, rootGuid: string, config: FolderItemConfig) => [
    'postFolderConfig',
    projectId,
    rootGuid,
    config.item_guid,
    JSON.stringify(config),
  ],
  request: async (
    projectId: string,
    rootGuid: string,
    config: FolderItemConfig,
  ): Promise<SectionItem> => {
    const result = await patchRequest<SectionItem>({
      url: `/projects/${projectId}/files/${rootGuid}/${config.item_guid}/folder-data`,
      data: { name: config.name, additional_data: config },
    })

    return result.data as SectionItem
  },
}

export const addTemplates: RequestTypeVariable = {
  getKey: (projectId: string, rootGuid: string, templates: TemplatesResult) => [
    'addTemplates',
    projectId,
    rootGuid,
    JSON.stringify(templates),
  ],
  request: async (
    projectId: string,
    rootGuid: string,
    templates: TemplatesResult & {
      move_custom_files_to_template_position: boolean
    },
  ): Promise<FilesResult> => {
    const { data } = await postRequest<FilesResult>({
      url: `/projects/${projectId}/files/${rootGuid}/templates`,
      data: {
        templates,
      },
    })

    return data as FilesResult
  },
}

export const postFileMetadata: RequestTypeVariable = {
  getKey: (projectId: string, rootGuid: string, config: FileItemConfig) => [
    'postFileMetadata',
    projectId,
    rootGuid,
    config.item_guid,
    JSON.stringify(config),
  ],
  request: async (
    projectId: string,
    rootGuid: string,
    config: FileItemConfig,
  ): Promise<FileItem> => {
    const result = await patchRequest<FileItem>({
      url: `/projects/${projectId}/files/${rootGuid}/${config.item_guid}/file-data`,
      data: { name: config.name, additional_data: config },
    })

    return result.data as FileItem
  },
}

export const deleteProjectFile: RequestTypeVariable = {
  getKey: (projectId: string, rootGuid: string, fileId: string) => [
    'deleteProjectFile',
    projectId,
    rootGuid,
    fileId,
  ],
  request: async (id: string, rootGuid: string, fileId: string): Promise<string[]> => {
    const { data } = await deleteRequest<string[]>({
      url: `/projects/${id}/files/${rootGuid}/${fileId}/files`,
    })

    return data as string[]
  },
}

export const deleteProjectFolder: RequestTypeVariable = {
  getKey: (projectId: string, rootGuid: string, folderId: string) => [
    'deleteProjectFolder',
    projectId,
    rootGuid,
    folderId,
  ],
  request: async (id: string, rootGuid: string, folderId: string): Promise<string[]> => {
    const { data: result } = await deleteRequest({
      url: `/projects/${id}/files/${rootGuid}/${folderId}/folders`,
    })

    return result as string[]
  },
}

export const changeFileOrder: RequestTypeVariable = {
  getKey: (projectId: string, rootGuid: string, documentOrder: FileOrder[]) => [
    'changeFileOrder',
    projectId,
    rootGuid,
    JSON.stringify(documentOrder),
  ],
  request: async (
    projectId: string,
    rootGuid: string,
    documentOrder: FileOrder[],
  ): Promise<void> => {
    await postRequest({
      url: `/projects/${projectId}/files/${rootGuid}/order`,
      data: documentOrder,
    })
  },
}

export const generateTemplatePreview: RequestTypeVariable = {
  getKey: (projectId: string, rootGuid: string, fileGuid: string) => [
    'generateTemplatePreview',
    projectId,
    rootGuid,
    fileGuid,
  ],
  request: async (
    projectId: string,
    rootGuid: string,
    fileGuid: string,
    addBaseHeaderFooter: boolean,
  ): Promise<{ task_id: string }> => {
    const { data } = await postRequest<{ task_id: string } | null>({
      url: `/projects/${projectId}/files/${rootGuid}/templates/generate/${fileGuid}/compute`,
      data: {
        add_base_header_footer: addBaseHeaderFooter,
      },
    })

    return data as { task_id: string }
  },
}

export const unlinkProjectFile: RequestTypeVariable = {
  getKey: (projectId: string, rootGuid: string, fileGuid: string) => [
    'unlinkProjectFile',
    projectId,
    rootGuid,
    fileGuid,
  ],
  request: async (projectId: string, rootGuid: string, fileGuid: string): Promise<FileItem> => {
    const { data } = await postRequest<FileItem>({
      url: `/projects/${projectId}/files/${rootGuid}/file/${fileGuid}/unlink`,
    })

    return data as FileItem
  },
}

export const updateFile: RequestTypeVariable = {
  getKey: (projectId: string, rootGuid: string, fileGuid: string) => [
    'updateFile',
    projectId,
    rootGuid,
    fileGuid,
  ],
  request: async (
    projectId: string,
    rootGuid: string,
    fileGuid: string,
    data: Blob,
  ): Promise<FileItem> => {
    const formData = new FormData()
    formData.append('file', data)

    const { data: result } = await patchRequest<FileItem>({
      url: `/projects/${projectId}/files/${rootGuid}/${fileGuid}`,
      data: formData,
    })

    return result as FileItem
  },
}
